import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import Login from "../Login/component";
import { fetchSharedVenueInfo } from "./api";
import type { SharedVenueInfo } from "./types";
import VenueHireBooking from "./VenueHireBooking";

interface SharedVenueHirePageProps {
  loggedIn: boolean;
  setLoggedIn: (loggedIn: boolean) => void;
}

const SharedVenueHirePage: React.FC<SharedVenueHirePageProps> = ({
  loggedIn,
  setLoggedIn,
}) => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [info, setInfo] = useState<SharedVenueInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInfo = useCallback(async () => {
    if (!shareToken || !loggedIn) return;
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      const data = await fetchSharedVenueInfo(shareToken);
      setInfo(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) {
        setNotFound(true);
        setInfo(null);
      } else {
        console.error("Error loading shared venue info:", err);
        setError("Failed to load booking information. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, [loggedIn, shareToken]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  if (!loggedIn) {
    return (
      <Box minHeight="100vh" bgcolor="#f5f4f0">
        <Container maxWidth="sm" sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sign in or create an Easy Agility account to book DIY venue slots
            through this link.
          </Alert>
        </Container>
        <Login setLoggedIn={setLoggedIn} />
      </Box>
    );
  }

  if (!shareToken) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Invalid booking link.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        minHeight="40vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          This booking link is no longer available.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!info) {
    return null;
  }

  return (
    <Box minHeight="100vh" bgcolor="#f5f4f0" py={3}>
      <Container maxWidth="lg">
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {info.club_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Book a DIY venue slot using the shared link from this club.
        </Typography>
        <VenueHireBooking
          clubId={info.club_id}
          clubName={info.club_name}
          venueHire={info.venue_hire}
          shareToken={shareToken}
          requiresName={info.requires_name}
        />
      </Container>
    </Box>
  );
};

export default SharedVenueHirePage;
